import React, { useMemo } from "react";

/**
 * RosaryBeads — an SVG rosary that highlights the currently-praying bead.
 *
 * Traditional Dominican rosary layout:
 *   Crucifix → 1 large bead (Apostles' Creed) → 3 small beads (Faith/Hope/Love)
 *   → 1 large bead (Our Father) → Center medallion
 *   → 5 decades around the loop, each: 1 large bead + 10 small beads
 *
 * Props:
 *   sequence   — full segment list from the API
 *   idx        — current segment index
 *
 * We compute:
 *   phase       — 'opening' | 'decade' | 'closing'
 *   decade      — 1..5 (only during decade phase)
 *   beadInDec   — 0 (Our Father) | 1..10 (Hail Marys) | 11 (Glory Be)
 *   introPos    — 0..4  when in opening phase
 */

const RosaryBeads = ({ sequence, idx }) => {
    const state = useMemo(() => {
        if (!sequence || !sequence.length) return null;
        const seg = sequence[idx];
        if (!seg) return null;

        // ── OPENING (crucifix, creed, 3 hail marys, glory be, opening our father) ──
        if (seg.phase === "opening" || seg.phase === "sign_of_cross") {
            // Count opening leader segments before current idx to map to intro bead
            let openingBeadIdx = 0;
            for (let i = 0; i < idx; i++) {
                const s = sequence[i];
                if (!s) continue;
                if (s.phase !== "opening" && s.phase !== "sign_of_cross") break;
                // Advance bead marker on each new "opening" leader segment
                if (s.role === "leader") openingBeadIdx++;
            }
            return { phase: "opening", introPos: Math.min(openingBeadIdx, 4) };
        }

        // ── CLOSING ────────────────────────────────────────
        if (seg.phase === "closing") {
            return { phase: "closing" };
        }

        // ── DECADE ─────────────────────────────────────────
        if (seg.phase === "decade" && seg.decade) {
            // Count Hail Marys we've LEFT within this decade so far
            // (each Hail Mary block has 2 segments: leader + response)
            let hailCount = 0;
            let inOurFather = true;   // between announce and first Hail Mary
            let inGloryBe = false;

            // Walk backwards from idx to the decade's start
            for (let i = idx; i >= 0; i--) {
                const s = sequence[i];
                if (!s || s.phase !== "decade" || s.decade !== seg.decade) break;
            }

            // Forward-scan from decade start
            let decStart = idx;
            for (let i = idx - 1; i >= 0; i--) {
                if (!sequence[i] || sequence[i].phase !== "decade" || sequence[i].decade !== seg.decade) {
                    decStart = i + 1;
                    break;
                }
                if (i === 0) decStart = 0;
            }

            let currentPrayerType = "announce"; // announce → our_father → hail_mary(1..10) → glory_be → fatima
            let hmIndex = 0;

            for (let i = decStart; i <= idx; i++) {
                const s = sequence[i];
                if (!s) continue;
                const label = (s.label?.en || "").toLowerCase();
                if (label.includes("announce") || label.includes("mystery")) {
                    currentPrayerType = "announce";
                } else if (label.includes("our father") || label.includes("pater noster")) {
                    currentPrayerType = "our_father";
                } else if (label.includes("hail mary") || label.includes("ave maria")) {
                    // Detect a new Hail Mary only on the leader role
                    if (s.role === "leader") {
                        if (currentPrayerType !== "hail_mary") {
                            currentPrayerType = "hail_mary";
                            hmIndex = 1;
                        } else if (i !== idx) {
                            // Only increment on transitions to new leader within Hail Mary run
                            // The pattern is leader → response → leader → response, so each new leader = next HM
                            hmIndex++;
                        }
                    }
                } else if (label.includes("glory be") || label.includes("gloria patri")) {
                    currentPrayerType = "glory_be";
                } else if (label.includes("fatima") || label.includes("o my jesus")) {
                    currentPrayerType = "fatima";
                }
            }

            let beadInDec = 0;
            if (currentPrayerType === "announce" || currentPrayerType === "our_father") beadInDec = 0;
            else if (currentPrayerType === "hail_mary") beadInDec = Math.max(1, Math.min(10, hmIndex));
            else if (currentPrayerType === "glory_be" || currentPrayerType === "fatima") beadInDec = 11;

            return { phase: "decade", decade: seg.decade, beadInDec };
        }

        return null;
    }, [sequence, idx]);

    if (!state) return null;

    // ── SVG geometry ────────────────────────────────────────
    // Circle loop: 5 decades × 11 slots (1 OF + 10 HM) = 55 beads, + 5 medallion pauses = 60 spots on loop
    // Center medallion at top-center of the loop. Intro chain drops from medallion.
    const cx = 200;
    const cy = 250;
    const R = 150;            // main loop radius
    const decadeSweep = (Math.PI * 2) / 5; // 72°
    const beadsPerDecade = 11; // 1 OF + 10 HM

    // Compute positions of loop beads
    const loopBeads = [];
    for (let d = 1; d <= 5; d++) {
        for (let b = 0; b < beadsPerDecade; b++) {
            // start from top-center (angle = -π/2), rotate clockwise
            const decadeStart = -Math.PI / 2 + (d - 1) * decadeSweep + decadeSweep * 0.06; // small gap after medallion
            const beadAngle = decadeStart + (b + 0.5) * (decadeSweep * 0.88 / beadsPerDecade);
            const x = cx + R * Math.cos(beadAngle);
            const y = cy + R * Math.sin(beadAngle);
            loopBeads.push({ decade: d, beadInDec: b, x, y, isOurFather: b === 0 });
        }
    }

    // Intro chain drops straight down from center medallion
    const medY = cy - R;  // top of the loop (12 o'clock)
    const introChain = [
        { key: "OF_open", x: cx, y: medY + 40, size: 8.5, kind: "of" },   // 1st Our Father (post-Creed)
        { key: "HM_3",    x: cx, y: medY + 70, size: 5.5, kind: "hm" },   // 3rd HM
        { key: "HM_2",    x: cx, y: medY + 100, size: 5.5, kind: "hm" },  // 2nd HM
        { key: "HM_1",    x: cx, y: medY + 130, size: 5.5, kind: "hm" },  // 1st HM (Faith)
        { key: "OF_creed",x: cx, y: medY + 160, size: 8.5, kind: "of" },  // Apostles' Creed bead
    ];
    const crucifixY = medY + 200;

    // Highlight logic
    const activeLoopBead = state.phase === "decade"
        ? loopBeads.find(lb => lb.decade === state.decade && lb.beadInDec === state.beadInDec)
        : null;
    const activeIntro = state.phase === "opening" ? introChain[state.introPos] : null;
    const activeCrucifix = state.phase === "opening" && state.introPos === 0 && !activeIntro;

    // Completed beads: everything BEFORE the current one in prayer-order
    const isCompleted = (lb) => {
        if (state.phase === "closing") return true;
        if (state.phase !== "decade") return false;
        if (lb.decade < state.decade) return true;
        if (lb.decade === state.decade) return lb.beadInDec < state.beadInDec;
        return false;
    };

    // ── STYLING ───────────────────────────────────────────
    const beadColor = "var(--color-liturgy, #7f1d1d)";
    const beadStroke = "rgba(0,0,0,0.15)";
    const dimColor = "rgba(0,0,0,0.35)";
    const chainColor = "rgba(0,0,0,0.35)";

    return (
        <div className="flex flex-col items-center" data-testid="rosary-beads">
            <svg viewBox="0 0 400 500" className="w-full max-w-[280px] h-auto" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.08))" }}>
                <defs>
                    <radialGradient id="beadGrad" cx="35%" cy="35%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
                        <stop offset="55%" stopColor="rgba(255,255,255,0.15)" />
                        <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
                    </radialGradient>
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3.5" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Main loop chain (subtle circle) */}
                <circle cx={cx} cy={cy} r={R} fill="none" stroke={chainColor} strokeWidth="1" opacity="0.5" />

                {/* Center medallion at the top of the loop */}
                <circle cx={cx} cy={medY} r="12" fill={beadColor} stroke={beadStroke} strokeWidth="1" />
                <circle cx={cx} cy={medY} r="12" fill="url(#beadGrad)" />
                <text x={cx} y={medY + 3.5} textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.85)" fontFamily="serif">✚</text>

                {/* Intro chain (straight line down) */}
                <line x1={cx} y1={medY + 12} x2={cx} y2={crucifixY - 10} stroke={chainColor} strokeWidth="1" opacity="0.5" />

                {/* Intro chain beads */}
                {introChain.map((b, i) => {
                    const isActive = activeIntro && activeIntro.key === b.key;
                    const isPassed = state.phase !== "opening" || (activeIntro && introChain.findIndex(x => x.key === activeIntro.key) > i);
                    return (
                        <g key={b.key}>
                            <circle
                                cx={b.x}
                                cy={b.y}
                                r={b.size}
                                fill={isActive ? beadColor : (isPassed ? beadColor : "transparent")}
                                stroke={isActive ? beadColor : (isPassed ? beadStroke : dimColor)}
                                strokeWidth="1.2"
                                opacity={isPassed ? 0.9 : 0.6}
                                filter={isActive ? "url(#glow)" : undefined}
                            >
                                {isActive && (
                                    <animate attributeName="r" values={`${b.size};${b.size + 2};${b.size}`} dur="1.8s" repeatCount="indefinite" />
                                )}
                            </circle>
                            {(isActive || isPassed) && <circle cx={b.x} cy={b.y} r={b.size} fill="url(#beadGrad)" />}
                        </g>
                    );
                })}

                {/* Crucifix at the bottom of intro chain */}
                <g transform={`translate(${cx}, ${crucifixY}) scale(1.1)`}>
                    <line x1="-8" y1="0" x2="8" y2="0" stroke={beadColor} strokeWidth="2.2" strokeLinecap="round" />
                    <line x1="0" y1="-6" x2="0" y2="18" stroke={beadColor} strokeWidth="2.2" strokeLinecap="round" />
                </g>

                {/* Loop beads */}
                {loopBeads.map((lb, i) => {
                    const size = lb.isOurFather ? 8 : 5;
                    const isActive = activeLoopBead && lb === activeLoopBead;
                    const passed = isCompleted(lb);
                    return (
                        <g key={i}>
                            <circle
                                cx={lb.x}
                                cy={lb.y}
                                r={size}
                                fill={isActive ? beadColor : (passed ? beadColor : "transparent")}
                                stroke={isActive ? beadColor : (passed ? beadStroke : dimColor)}
                                strokeWidth="1.2"
                                opacity={passed ? 0.85 : (isActive ? 1 : 0.55)}
                                filter={isActive ? "url(#glow)" : undefined}
                            >
                                {isActive && (
                                    <animate attributeName="r" values={`${size};${size + 2.4};${size}`} dur="1.7s" repeatCount="indefinite" />
                                )}
                            </circle>
                            {(isActive || passed) && <circle cx={lb.x} cy={lb.y} r={size} fill="url(#beadGrad)" />}
                        </g>
                    );
                })}

                {/* Decade number markers (small roman numerals just inside each decade start) */}
                {[1,2,3,4,5].map(d => {
                    const angle = -Math.PI / 2 + (d - 1) * decadeSweep + decadeSweep * 0.5;
                    const x = cx + (R - 28) * Math.cos(angle);
                    const y = cy + (R - 28) * Math.sin(angle);
                    const highlight = state.phase === "decade" && state.decade === d;
                    return (
                        <text
                            key={d}
                            x={x}
                            y={y + 3.5}
                            textAnchor="middle"
                            fontSize="10"
                            fill={highlight ? beadColor : dimColor}
                            fontFamily="serif"
                            fontStyle="italic"
                            fontWeight={highlight ? 700 : 400}
                        >
                            {["I","II","III","IV","V"][d-1]}
                        </text>
                    );
                })}
            </svg>

            {/* Legend beneath */}
            <div className="mt-3 text-[10px] text-muted-foreground text-center italic" data-testid="rosary-beads-legend">
                {state.phase === "opening" && "Opening prayers"}
                {state.phase === "decade" && (
                    <>Decade {["I","II","III","IV","V"][state.decade-1]}
                        {" · "}
                        {state.beadInDec === 0
                            ? "Our Father"
                            : state.beadInDec === 11
                                ? "Glory Be"
                                : `Hail Mary ${state.beadInDec} of 10`}
                    </>
                )}
                {state.phase === "closing" && "Closing prayers"}
            </div>
        </div>
    );
};

export default RosaryBeads;
